import type {ClientConfigDiagnosticsResult, MediaDeviceSummary} from './types'

/** Same browser allow-list as Amazon Connect Endpoint Test Utility. */
const SUPPORTED_BROWSERS_REGEX = /chrome|firefox|edge/i

const detectOs = (
  userAgent: string
): {osName: string; osVersion: string} => {
  if (/windows nt/i.test(userAgent)) {
    const m = userAgent.match(/Windows NT ([0-9._]+)/i)
    return {osName: 'Windows', osVersion: (m?.[1] || '').replace(/_/g, '.')}
  }
  if (/android/i.test(userAgent)) {
    const m = userAgent.match(/Android ([0-9._]+)/i)
    return {osName: 'Android', osVersion: (m?.[1] || '').replace(/_/g, '.')}
  }
  if (/iphone|ipad|ipod/i.test(userAgent)) {
    const m = userAgent.match(/OS ([0-9_]+)/i)
    return {osName: 'iOS', osVersion: (m?.[1] || '').replace(/_/g, '.')}
  }
  if (/mac os x/i.test(userAgent)) {
    const m = userAgent.match(/Mac OS X ([0-9_]+)/i)
    return {osName: 'macOS', osVersion: (m?.[1] || '').replace(/_/g, '.')}
  }
  if (/linux/i.test(userAgent)) {
    return {osName: 'Linux', osVersion: '-'}
  }
  return {osName: 'Unknown', osVersion: '-'}
}

const detectBrowserName = (userAgent: string): string => {
  if (/edg\//i.test(userAgent)) return 'Microsoft Edge'
  if (/chrome\//i.test(userAgent) && !/edg\//i.test(userAgent)) return 'Google Chrome'
  if (/firefox\//i.test(userAgent)) return 'Firefox'
  if (/safari\//i.test(userAgent) && !/chrome\//i.test(userAgent)) return 'Safari'
  return 'Unknown'
}

const mapDevice = (device: MediaDeviceInfo): MediaDeviceSummary => ({
  kind: device.kind,
  // Labels are often empty without prior permission — that is expected on accept path.
  label: device.label || '(permission pending / unlabeled)',
  deviceId: device.deviceId,
  groupId: device.groupId,
})

/**
 * Client/workstation snapshot without calling getUserMedia.
 * Safe to run during call accept (no mic prompt / track steal).
 */
export const collectClientConfig = async (): Promise<ClientConfigDiagnosticsResult> => {
  const errorMessages: string[] = []
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  const onLine = typeof navigator !== 'undefined' ? navigator.onLine : true
  const {osName, osVersion} = detectOs(userAgent)
  const browserName = detectBrowserName(userAgent)
  const isSupportedBrowser = SUPPORTED_BROWSERS_REGEX.test(browserName)
  const rtcSupport =
    typeof RTCPeerConnection !== 'undefined' ||
    (typeof window !== 'undefined' &&
      typeof (window as unknown as {webkitRTCPeerConnection?: unknown}).webkitRTCPeerConnection !==
        'undefined')
  const isMobileDevice = /Mobi|Android|iPhone|iPad/i.test(userAgent)

  let audioInputDevices: MediaDeviceSummary[] = []
  let audioOutputDevices: MediaDeviceSummary[] = []
  let videoInputDevices: MediaDeviceSummary[] = []
  let hasMicrophoneAccess: boolean | null = null
  let hasCameraAccess: boolean | null = null

  try {
    if (navigator.mediaDevices?.enumerateDevices) {
      const devices = await navigator.mediaDevices.enumerateDevices()
      audioInputDevices = devices.filter((d) => d.kind === 'audioinput').map(mapDevice)
      audioOutputDevices = devices.filter((d) => d.kind === 'audiooutput').map(mapDevice)
      videoInputDevices = devices.filter((d) => d.kind === 'videoinput').map(mapDevice)

      // If any input has a non-empty label, permission was previously granted.
      const labeledMics = audioInputDevices.filter((d) => d.label && !d.label.startsWith('('))
      const labeledCams = videoInputDevices.filter((d) => d.label && !d.label.startsWith('('))
      if (audioInputDevices.length > 0) {
        hasMicrophoneAccess = labeledMics.length > 0
      }
      if (videoInputDevices.length > 0) {
        hasCameraAccess = labeledCams.length > 0
      }
    } else {
      errorMessages.push('enumerateDevices not supported')
    }
  } catch (err) {
    errorMessages.push(err instanceof Error ? err.toString() : String(err))
  }

  const hasMicrophone = audioInputDevices.length > 0
  const hasSpeakers = audioOutputDevices.length > 0
  const hasCamera = videoInputDevices.length > 0

  if (!onLine) errorMessages.push('Not online / No connectivity')
  if (!rtcSupport) errorMessages.push('WebRTC not supported')
  if (!isSupportedBrowser) errorMessages.push('Browser not in Connect supported list (chrome|firefox|edge)')
  if (!hasMicrophone) errorMessages.push('No microphone detected')
  if (hasMicrophone && hasMicrophoneAccess === false) {
    errorMessages.push('No microphone access detected')
  }
  if (!hasCamera) errorMessages.push('No camera detected')
  if (hasCamera && hasCameraAccess === false) {
    errorMessages.push('No camera access detected')
  }
  if (!hasSpeakers) errorMessages.push('No speakers detected')

  return {
    onLine,
    osName,
    osVersion,
    browserName,
    userAgent,
    isSupportedBrowser,
    rtcSupport,
    isMobileDevice,
    hasSpeakers,
    hasMicrophone,
    hasMicrophoneAccess,
    hasCamera,
    hasCameraAccess,
    audioInputDevices,
    audioOutputDevices,
    videoInputDevices,
    errorMessages,
  }
}

/** Client issues that should elevate overall diagnostics severity. */
export const clientConfigSeverityTier = (
  config: ClientConfigDiagnosticsResult
): 'success' | 'warning' | 'failure' => {
  if (!config.rtcSupport || !config.onLine) return 'failure'
  if (!config.isSupportedBrowser) return 'warning'
  if (!config.hasMicrophone || config.hasMicrophoneAccess === false) return 'warning'
  return 'success'
}
